import lodash from 'lodash';
import { StrHlp } from 'src/lib/fliko/helpers/string.helper';
import { LANG_CODES, LangHr } from 'src/lib/fliko/models/translation/generic';
import { Collection } from 'src/lib/fliko/types/database.type';
import { SelectInputData } from '../interfaces/select-input-data.interface';
import { LocalizationService } from '../services/localization.service';

export class SelectInputHelper {
  /**
   * Map collection to selectInputData array
   * @param {T[]}               source   collection to map
   * @param {string | function} getTitle path to title field or parser function
   * @param {string | function} getValue path to value field or parser function
   * @return {SelectInputData<U>[]}
   */
  static fromObjects<T, U = string>(
    source: Iterable<T>,
    getTitle: string | ((x: T) => string) = 'name',
    getValue: string | ((x: T) => U) = 'id'
  ): SelectInputData<U>[] {
    return Array.from(source, (elem) => {
      return {
        title: typeof getTitle == 'function' ? getTitle(elem) : lodash.get(elem, getTitle),
        value: typeof getValue == 'function' ? getValue(elem) : lodash.get(elem, getValue)
      };
    });
  }

  /**
   * Map collection to SelectInputData array
   * @param {Iterable<T>} source collection to map
   * @param {function}    parser [default: toString()] function used to convert value to title
   * @return SelectInputData array, where title = value = stringValue
   */
  public static fromValues<T>(
    source: Iterable<T>,
    parser: (x: any) => string = (x) => x?.toString() ?? ''
  ): SelectInputData<T>[] {
    return Array.from(source, (v) => {
      return { title: parser(v), value: v };
    });
  }

  /**
   * Map enum to SelectInputData array
   * @param {Collection<T>} enumType
   * @param {string}        translationPrefix
   * @return
   */
  public static fromEnum<T>(enumType: Collection<T>, translationPrefix: string): SelectInputData<T>[] {
    return Object.values(enumType).map((value) => {
      return { title: `${translationPrefix}.${value}`, value };
    });
  }

  /**
   * Get 'all' select input with given title
   * @param {string}                     title title key to translate
   * @param {LocalizationService | null} ls    localization service if you want to translate the title
   * @return {SelectInputData<string>} with value: 'all' and given title
   */
  static allIndicator(title: string, ls: LocalizationService | null): SelectInputData<string> {
    return { title: StrHlp.capitalize(ls ? ls.translate(title) : title), value: 'all' };
  }

  /**
   * Compare titles of two select inputs
   * @param {SelectInputData} a
   * @param {SelectInputData} b
   * @return
   */
  static cmpTitle(a: SelectInputData<unknown>, b: SelectInputData<unknown>): number {
    return a.title.localeCompare(b.title);
  }
}

/**
 * Factory allowing to build select input from elements added in time
 */
export class SelectInputFromObjectsBuilder<S, T> {
  private data = new Map<T, S>();

  private getTitle: (x: S) => string;
  private getValue: (x: S) => T;

  /**
   * @param {string | function} getTitle path to title field or parser function
   * @param {string | function} getValue path to value field or parser function
   */
  constructor(getTitle: string | ((x: S) => string) = 'name', getValue: string | ((x: S) => T) = 'id') {
    // eslint-disable-next-line
    // @ts-expect-error
    this.getTitle = StrHlp.isString(getTitle) ? (x) => lodash.get(x, getTitle) : getTitle;
    // eslint-disable-next-line
    // @ts-expect-error
    this.getValue = StrHlp.isString(getValue) ? (x) => lodash.get(x, getValue) : getValue;
  }

  /**
   * Adds new data element to builder
   * @param {S} value
   */
  public add(value: S): void {
    this.data.set(this.getValue(value), value);
  }

  /**
   * Builds array of SelectInputData from accumulated data
   * @return {SelectInputData<T>[]}
   */
  public build(): SelectInputData<T>[] {
    return SelectInputHelper.fromObjects<[T, S], T>(this.data, (x) => this.getTitle(x[1]), '0').sort(SelectInputHelper.cmpTitle);
  }
}

/**
 * Factory allowing to build select input from elements added in time
 */
export class SelectInputFromValuesBuilder<T> {
  private data = new Set<T>();

  /** @param parser [default: toString()]] function used to convert value to title */
  constructor(private parser: (x: any) => string = (x) => x?.toString() ?? '-') {}

  /**
   * Adds new data element to builder
   * @param {T} value
   */
  public add(value: T) {
    this.data.add(value);
  }

  /**
   * Builds array of SelectInputData from accumulated data
   * @return {SelectInputData<T>[]}
   */
  public build(): SelectInputData<T>[] {
    return SelectInputHelper.fromValues(this.data, this.parser).sort(SelectInputHelper.cmpTitle);
  }
}

export const LANG_CODES_OPTIONS = SelectInputHelper.fromValues(LANG_CODES, (x) => `${LangHr(x)} [${x}]`);
